/**
 * Logout Toast Suppressor — Feb 25, 2026.
 *
 * Reason: when an authenticated user clicks Logout, the flow is:
 *
 *   1. POST /api/auth/logout         (token blacklisted server-side)
 *   2. setUser(null) + clear axios   (synchronous)
 *   3. clear localStorage            (synchronous)
 *   4. window.location.replace('/login')   (async navigation)
 *
 * Any polling component (notifications, dashboard stats, break button,
 * downtime, edit-requests, activity feed) whose interval fires between
 * #1 and #4 will get a 401/403 from the backend (token blacklisted or
 * Authorization header now missing). Most of those error handlers call
 * `toast.error(err.message)` — which surfaces "Permission denied" /
 * "Authentication failed" as a quick flash toast right before the
 * page navigates, confusing the user.
 *
 * Fix: at logout start, monkey-patch sonner's `toast.error` / `.warning`
 * to a no-op, AND dismiss any toasts already on screen. Patches are NOT
 * restored — `window.location.replace` reloads the JS bundle on the
 * /login page anyway, so the originals come back fresh.
 *
 * Single point of intervention; no need to touch the 90+ files that
 * import `toast` from sonner directly.
 */
import { toast } from 'sonner';

let _suppressed = false;

export const suppressToastsDuringLogout = () => {
  if (_suppressed) return;
  _suppressed = true;
  try {
    // Clear anything currently on screen so the user doesn't see a
    // late-arriving toast from a poll that resolved milliseconds ago.
    if (typeof toast.dismiss === 'function') toast.dismiss();
  } catch (_) { /* noop */ }
  try {
    // No-op the error/warning/info/success channels for the brief
    // navigation window. We deliberately DO NOT patch `toast.custom`
    // because the unified logout notice (showGlassmorphismLogoutNotice)
    // uses `toast.custom` on the new /login bundle. Patching it here
    // can leak into that path on soft navigations and silently drop
    // the toast.
    toast.error = () => {};
    toast.warning = () => {};
    toast.info = () => {};
    toast.success = () => {};
  } catch (_) { /* sonner is frozen in some environments — best effort */ }

  // Belt-and-suspenders flag for any custom error handler that wants to
  // explicitly check before firing a side-effect.
  try { window.__titleflowIsLoggingOut__ = true; } catch (_) { /* noop */ }
};

export const isLoggingOut = () => !!window.__titleflowIsLoggingOut__;
